import { BURN_TICK_SECONDS, type ClassId } from '../core/constants.ts';
import { ROMAN } from '../core/progression.ts';
import {
  SLOT_ORDER, spellsFor, type SlotRole, type SpellDef,
} from '../core/spells.ts';

/**
 * The six slots and everything that can fill them.
 *
 * Pulled out of the setup screen because the lobby needs the same thing and
 * there is no version of "the same thing" that survives being written twice.
 * Two copies of a panel this dense would drift within a week — a number
 * formatted differently here, a spell's burn duration shown there and not here —
 * and the drift would be invisible until somebody compared them side by side.
 *
 * What differs between the two callers is the framing: the muster wraps it in a
 * page with a title and a way onward, and the lobby throws it up over the room
 * with a way back. So this returns the index and nothing else, and the framing
 * belongs to whoever asked for it.
 */

const SLOT_LABEL: Record<SlotRole, string> = {
  // Never shown: innates are not chosen, so the grimoire never offers them.
  innate: 'Innate',
  primary: 'Primary',
  secondary: 'Secondary',
  area: 'Area',
  control: 'Control',
  utility: 'Utility',
  defense: 'Defense',
};

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K, cls?: string, text?: string,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
};

/** "2d6 → 4d8", or an em dash for the spells that do not roll. */
export function diceLine(spell: SpellDef): string {
  if (!spell.dice) return '—';
  const first = spell.dice[0]!;
  const last = spell.dice[spell.dice.length - 1]!;
  return `${first.count}d${first.sides} → ${last.count}d${last.sides}`;
}

/**
 * Builds the index of slots and candidates.
 *
 * `onPick` is given the slot's position and the spell chosen. The caller owns
 * the loadout and redraws — this holds no state of its own, which is what lets
 * the lobby show a loadout that is really the room's and the muster show one
 * that is really the draft's, without either knowing about the other.
 */
export function buildGrimoire(
  cls: ClassId,
  loadout: readonly string[],
  onPick: (slot: number, spellId: string) => void,
): HTMLElement {
  const index = el('div', 'grimoire');

  SLOT_ORDER.forEach((slot, i) => {
    const candidates = spellsFor(cls, slot);
    const row = el('div', 'grimoire-row');

    const head = el('div', 'grimoire-head');
    head.appendChild(el('span', 'grimoire-key', String(i + 1)));
    head.appendChild(el('span', 'grimoire-slot', SLOT_LABEL[slot]));
    row.appendChild(head);

    const options = el('div', 'grimoire-options');
    // One candidate is not a choice, and dressing it as one is a lie.
    options.dataset.fixed = String(candidates.length < 2);

    for (const spell of candidates) {
      const opt = el('button', 'spell-option');
      opt.type = 'button';
      opt.dataset.on = String(loadout[i] === spell.id);

      const top = el('span', 'spell-top');
      top.appendChild(el('span', 'spell-name', spell.name));
      top.appendChild(el('span', 'spell-school', spell.school));
      opt.appendChild(top);

      const nums = el('span', 'spell-nums');
      nums.appendChild(el('span', '', diceLine(spell)));
      nums.appendChild(el('span', '', `${spell.ppCost[0]}–${spell.ppCost[4]} p`));
      nums.appendChild(el('span', '', `${spell.cooldown}s`));

      // The two things that do not show up in the dice at all.
      if (spell.burnTicks) {
        nums.appendChild(el(
          'span', 'spell-extra',
          `burns ${(spell.burnTicks[0]! * BURN_TICK_SECONDS).toFixed(1)}`
          + `–${(spell.burnTicks[4]! * BURN_TICK_SECONDS).toFixed(1)}s`,
        ));
      }
      if (spell.ppDrain) {
        nums.appendChild(el(
          'span', 'spell-extra',
          `drains ${spell.ppDrain[0]}–${spell.ppDrain[4]} p`,
        ));
      }
      opt.appendChild(nums);

      /*
       * Only the tiers where the name actually changes. Void Ball keeps its name
       * for four tiers and becomes Utter Void at the fifth; listing all five
       * would be four repetitions and one fact.
       */
      if (spell.tierNames) {
        const steps: string[] = [];
        spell.tierNames.forEach((n, t) => {
          if (t > 0 && n === spell.tierNames![t - 1]) return;
          steps.push(`${ROMAN[t]} ${n}`);
        });
        if (steps.length > 1) opt.appendChild(el('span', 'spell-ladder', steps.join(' · ')));
      }
      if (spell.notes) opt.appendChild(el('span', 'spell-note', spell.notes));

      opt.addEventListener('click', () => onPick(i, spell.id));
      options.appendChild(opt);
    }

    row.appendChild(options);
    index.appendChild(row);
  });

  return index;
}

/**
 * Throws the grimoire up over whatever is behind it, with a way back.
 *
 * The lobby's use. It is a screen rather than a panel because six slots of
 * candidates is more than fits beside a room, and because choosing spells is a
 * thing somebody does deliberately and then stops doing — which is exactly what
 * a screen you leave is for.
 */
export function openGrimoire(
  cls: ClassId,
  loadout: string[],
  onDone: (loadout: string[]) => void,
): void {
  const draft = [...loadout];

  const veil = el('div', 'grimoire-veil');
  const sheet = el('div', 'setup-sheet grimoire-sheet');
  veil.appendChild(sheet);

  const close = (): void => {
    veil.remove();
    onDone(draft);
  };

  const draw = (): void => {
    sheet.innerHTML = '';
    sheet.appendChild(el('div', 'setup-eyebrow', cls));
    sheet.appendChild(el('h1', 'setup-title', 'Choose your spells'));
    sheet.appendChild(buildGrimoire(cls, draft, (slot, id) => {
      draft[slot] = id;
      draw();
    }));

    const actions = el('div', 'setup-actions');
    const done = el('button', 'setup-go', 'Back to the lobby');
    done.type = 'button';
    done.addEventListener('click', close);
    actions.appendChild(done);
    sheet.appendChild(actions);
  };

  draw();
  document.body.appendChild(veil);
}
